/**
 * Password-recovery redirects (`/auth?type=recovery` and/or hash `type=recovery`).
 * Capture before `createClient` — `detectSessionInUrl` clears the hash, which is why
 * the set-new-password screen used to miss the link and fall through to sign-in.
 */

const STORAGE_KEY = 'bravos.passwordRecovery';

let capturedRecoveryFlag = false;

function hashAndQueryParams(urlLike?: string): { hashParams: URLSearchParams; queryParams: URLSearchParams } {
  if (typeof urlLike === 'string') {
    try {
      const u = new URL(urlLike, 'https://example.invalid');
      const hashRaw = u.hash.startsWith('#') ? u.hash.slice(1) : u.hash;
      return {
        hashParams: new URLSearchParams(hashRaw),
        queryParams: u.searchParams,
      };
    } catch {
      return { hashParams: new URLSearchParams(), queryParams: new URLSearchParams() };
    }
  }
  if (typeof window === 'undefined') {
    return { hashParams: new URLSearchParams(), queryParams: new URLSearchParams() };
  }
  const hashRaw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  return {
    hashParams: new URLSearchParams(hashRaw),
    queryParams: new URLSearchParams(window.location.search),
  };
}

function readStoredFlag(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredFlag(on: boolean): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (on) sessionStorage.setItem(STORAGE_KEY, '1');
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** True when the URL indicates a password-recovery callback. */
export function urlIndicatesPasswordRecovery(urlLike?: string): boolean {
  const { hashParams, queryParams } = hashAndQueryParams(urlLike);
  const type = (queryParams.get('type') || hashParams.get('type') || '').toLowerCase();
  return type === 'recovery';
}

/** Snapshot recovery from the current URL before `createClient`. Call once at app boot. */
export function capturePasswordRecoveryFromLocation(): boolean {
  if (urlIndicatesPasswordRecovery()) {
    capturedRecoveryFlag = true;
    writeStoredFlag(true);
  }
  return capturedRecoveryFlag || readStoredFlag();
}

/** True if this page load was a recovery redirect (live URL, boot capture, or sessionStorage). */
export function wasPasswordRecoveryRedirect(): boolean {
  return urlIndicatesPasswordRecovery() || capturedRecoveryFlag || readStoredFlag();
}

/** Clear after a successful password update or when the user abandons the reset. */
export function clearPasswordRecoveryCapture(): void {
  capturedRecoveryFlag = false;
  writeStoredFlag(false);
}
